// ============================================================================
// Fleet & Assets data hooks — flat-fetch over vehicles / floor_machines /
// vehicle_mileage_readings / asset_costs (RLS scopes rows per role). Client-side
// join + aggregation, matching the app's flat-fetch convention. Costs are
// manual/rule-tagged now; the CC card feed is a later phase (schema is ready).
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { commitWrite, useReader } from '../../../shared/save-path';
import { rpcErrorMessage } from '../../../shared/rpc-error';
import { fetchAmNameMap } from '../../../shared/am-name';
import { ACTIVE_VEHICLES_PGRST_FILTER, isActiveVehicleStatus } from '../vehicle-status';

// ----- Row shapes -----------------------------------------------------------

export interface VehicleRow {
  vehicle_id: string;
  assigned_employee_eid: string | null;
  location: string | null;
  year: number | null;
  make_model: string | null;
  vin: string | null;
  mileage: number | null;
  status: string | null;
  registration_exp: string | null;
  insurance_provider: string | null;
  insurance_exp: string | null;
  emissions_exp: string | null;
  license_plate: string | null;
  plate_state: string | null;
  powertrain: string | null;
  toll_pass: boolean;
  has_home_charger: boolean;
  last_mileage_at: string | null;
  last_mileage_by_eid: string | null;
  last_maintenance_date: string | null;
  next_maintenance_due: string | null;
  color: string | null;
  notes: string | null;
}

/**
 * What the app may WRITE to a vehicle — the create/update input, as distinct
 * from VehicleRow, which is the read shape.
 *
 * `vehicle_id` is absent by construction, not merely optional. The BEFORE
 * INSERT trigger trg_vehicles_assign_id mints it from next_prefixed_id('VEH'),
 * and it tolerates both a null and a blank — but omission is the clearer
 * contract and it makes a blank string unrepresentable rather than merely
 * unlikely. On the update path the id travels as saveVehicle's first argument,
 * never in the patch: a primary key is not something a form edits.
 */
export type VehicleWritePatch = Partial<Omit<VehicleRow, 'vehicle_id'>>;

/**
 * A floor machine AS READ. This is the shape of `v_floor_machines`, not of the
 * `floor_machines` table, and every read in this module goes through the view.
 *
 * WHY THE VIEW AND NOT THE TABLE. The table stores `facility_id` /
 * `office_location_id` — ids. Cards were rendering `OF-001` where a person
 * needs to read `KC Headquarters`, and the alternative to the view is every
 * surface fetching two more tables and re-deriving the same display string,
 * which is how two surfaces end up disagreeing about what a place is called.
 * The view applies the naming rule ONCE: a facility renders as
 * `Facility Name (Owner Name)`, an office as its own name. Do not re-derive it.
 *
 * WRITES ARE UNAFFECTED. `floor_machines` revokes INSERT/UPDATE/DELETE from
 * `authenticated`; every write is an RPC (see hooks/use-asset-location.ts), so
 * reading a view costs nothing on the write side.
 */
export interface MachineRow {
  machine_id: string;
  assigned_employee_eid: string | null;
  /** Denormalised on the base table. Prefer the EID → name map where one is
   *  already loaded; this is the fallback, not a second source of truth. */
  assigned_employee_name: string | null;
  /** Structured location — exactly one of these two is set (0 of 26 rows have
   *  both or neither). These are authoritative but are NOT what is rendered. */
  facility_id: string | null;
  office_location_id: string | null;
  /** 'facility' | 'office' | 'unassigned'. Derived by the view from the two
   *  columns above, so a surface never has to work out which one is set. */
  location_kind: string | null;
  /** COALESCE(facility_id, office_location_id). Secondary text or a tooltip
   *  ONLY — never the primary value a person reads. */
  location_id: string | null;
  location_name: string | null;
  location_owner_name: string | null;
  /** ⛔ THE ONE STRING TO RENDER. Already carries the naming rule, and already
   *  says 'Unassigned' when there is no location at all. */
  location_display: string | null;
  location_region: string | null;
  office_address: string | null;
  /** Written only by rpc_asset_confirm_location / _move / _set_status. */
  last_confirmed_at: string | null;
  last_confirmed_by_eid: string | null;
  /** ⛔ RECONCILIATION ONLY — NEVER RENDER THIS. Stale free text from before
   *  the structured columns existed, kept so the two can be compared. It
   *  disagrees with the record it sits beside: FM-0002 says "Irving Office"
   *  where the office record is "Dallas Office", FM-0024 says "Dallas Sport"
   *  for a facility now named "BS&W Sports SC". It drifted precisely because
   *  the Add/Edit modal wrote here and nowhere else. */
  legacy_location_text: string | null;
  machine_brand: string | null;
  machine_model: string | null;
  serial_number: string | null;
  status: string | null;
  last_maintenance_date: string | null;
  next_maintenance_due: string | null;
  purchase_date: string | null;
  warranty_exp: string | null;
  notes: string | null;
}

/** How a machine's location is rendered, everywhere, with no exceptions.
 *  One function so the "names not ids" rule is enforced by the type system
 *  rather than remembered at each call site — a rule remembered at twenty call
 *  sites is a rule that will be forgotten at the twenty-first (TCSD-0129). */
export function machineLocation(m: MachineRow | null | undefined): {
  display: string;
  kind: 'facility' | 'office' | 'unassigned';
  /** For muted secondary text or a tooltip. Null when there is no location. */
  id: string | null;
} {
  const kind =
    m?.location_kind === 'facility' || m?.location_kind === 'office'
      ? m.location_kind
      : 'unassigned';
  return {
    display: (kind === 'unassigned' ? 'Unassigned' : m?.location_display) || 'Unassigned',
    kind,
    id: kind === 'unassigned' ? null : m?.location_id ?? null,
  };
}

export interface ReadingRow {
  reading_id: string;
  vehicle_id: string;
  odometer: number;
  read_on: string;
  reported_by_eid: string | null;
  source: string;
  note: string | null;
}

export interface CostRow {
  cost_id: string;
  vehicle_id: string | null;
  machine_id: string | null;
  employee_eid: string | null;
  cost_category: string;
  amount: number | string;
  currency: string;
  incurred_on: string;
  is_capital: boolean;
  region: string | null;
  vendor: string | null;
  source: string;
  external_ref: string | null;
  needs_review: boolean;
  note: string | null;
}

// ----- Helpers --------------------------------------------------------------

export const fmtMoney = (n: number | string | null | undefined) =>
  n == null
    ? '—'
    : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export const fmtDate = (d: string | null | undefined) =>
  !d ? '—' : new Date(d + (d.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

export function firstOfMonthISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/** Row-level convenience over the status vocabulary. The list of inactive
 *  values moved to ../vehicle-status so the query filter, the tiles, the card
 *  and the form all read one list — it used to live here as INACTIVE_STATUSES
 *  and nothing but this predicate could see it. */
export const isActiveVehicle = (v: { status: string | null }) =>
  isActiveVehicleStatus(v.status);

/** Average miles/month across a vehicle's readings (max-min over month span). */
export function avgMilesPerMonth(readings: ReadingRow[]): number | null {
  if (readings.length < 2) return null;
  const sorted = [...readings].sort((a, b) => a.read_on.localeCompare(b.read_on));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const months =
    (new Date(last.read_on).getTime() - new Date(first.read_on).getTime()) /
    (86_400_000 * 30.437);
  if (months <= 0) return null;
  const delta = last.odometer - first.odometer;
  if (delta < 0) return null;
  return Math.round(delta / months);
}

// ----- Rollup ---------------------------------------------------------------

/**
 * @param includeInactive  when false (the default view), sold/retired vehicles
 *   are excluded IN THE QUERY rather than fetched and discarded. The filter
 *   string is built from INACTIVE_VEHICLE_STATUSES so it cannot drift from the
 *   predicate the tiles use.
 */
export function useFleetRollup(includeInactive = false) {
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [machines, setMachines] = useState<MachineRow[]>([]);
  const [readings, setReadings] = useState<ReadingRow[]>([]);
  const [mtdCosts, setMtdCosts] = useState<CostRow[]>([]);
  const [nameMap, setNameMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const vQuery = supabase.from('vehicles').select('*').order('vehicle_id');
        if (!includeInactive) vQuery.or(ACTIVE_VEHICLES_PGRST_FILTER);
        const [vRes, mRes, rRes, cRes] = await Promise.all([
          vQuery,
          // The VIEW, never the table — it carries location_display. See
          // MachineRow.
          supabase.from('v_floor_machines').select('*').order('machine_id'),
          supabase.from('vehicle_mileage_readings').select('*').order('read_on', { ascending: false }),
          supabase.from('asset_costs').select('*').gte('incurred_on', firstOfMonthISO()),
        ]);
        if (vRes.error) throw vRes.error;
        if (mRes.error) throw mRes.error;
        if (rRes.error) throw rRes.error;
        if (cRes.error) throw cRes.error;
        if (cancelled) return;
        const vs = (vRes.data ?? []) as VehicleRow[];
        const ms = (mRes.data ?? []) as MachineRow[];
        setVehicles(vs);
        setMachines(ms);
        setReadings((rRes.data ?? []) as ReadingRow[]);
        setMtdCosts((cRes.data ?? []) as CostRow[]);
        const eids = [
          ...vs.map((v) => v.assigned_employee_eid),
          ...ms.map((m) => m.assigned_employee_eid),
        ];
        setNameMap(await fetchAmNameMap(eids));
      } catch (e) {
        if (!cancelled) setError(rpcErrorMessage(e, 'Could not load the fleet.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick, includeInactive]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { vehicles, machines, readings, mtdCosts, nameMap, loading, error, refetch };
}

// ----- Vehicle detail -------------------------------------------------------

export function useVehicleDetail(vehicleId: string | undefined) {
  const [vehicle, setVehicle] = useState<VehicleRow | null>(null);
  const [readings, setReadings] = useState<ReadingRow[]>([]);
  const [costs, setCosts] = useState<CostRow[]>([]);
  const [nameMap, setNameMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!vehicleId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [vRes, rRes, cRes] = await Promise.all([
          supabase.from('vehicles').select('*').eq('vehicle_id', vehicleId).maybeSingle(),
          supabase.from('vehicle_mileage_readings').select('*').eq('vehicle_id', vehicleId).order('read_on', { ascending: false }),
          supabase.from('asset_costs').select('*').eq('vehicle_id', vehicleId).order('incurred_on', { ascending: false }),
        ]);
        if (vRes.error) throw vRes.error;
        if (rRes.error) throw rRes.error;
        if (cRes.error) throw cRes.error;
        if (cancelled) return;
        const v = (vRes.data ?? null) as VehicleRow | null;
        setVehicle(v);
        setReadings((rRes.data ?? []) as ReadingRow[]);
        setCosts((cRes.data ?? []) as CostRow[]);
        setNameMap(await fetchAmNameMap([v?.assigned_employee_eid ?? null]));
      } catch (e) {
        if (!cancelled) setError(rpcErrorMessage(e, 'Could not load this vehicle.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vehicleId, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { vehicle, readings, costs, nameMap, loading, error, refetch };
}

// ----- Driver "my vehicle" --------------------------------------------------

export function useDriverVehicle(eid: string | null | undefined) {
  const [vehicle, setVehicle] = useState<VehicleRow | null>(null);
  const [lastReading, setLastReading] = useState<ReadingRow | null>(null);
  const [loading, setLoading] = useState(true);
  // A saved reading moves the vehicle (the database copies it), so re-read both.
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((n) => n + 1), []);
  useReader(['vehicles', 'vehicle_mileage_readings'], refetch);

  useEffect(() => {
    if (!eid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      // Only the first read shows loading; a re-read after a save keeps the tile up.
      if (tick === 0) setLoading(true);
      try {
        const { data, error } = await supabase
          .from('vehicles')
          .select('*')
          .eq('assigned_employee_eid', eid);
        if (error) throw error;
        const active = ((data ?? []) as VehicleRow[]).filter(isActiveVehicle);
        const v = active[0] ?? null;
        if (cancelled) return;
        setVehicle(v);
        if (v) {
          const { data: r } = await supabase
            .from('vehicle_mileage_readings')
            .select('*')
            .eq('vehicle_id', v.vehicle_id)
            .order('read_on', { ascending: false })
            .limit(1);
          if (!cancelled) setLastReading(((r ?? [])[0] as ReadingRow) ?? null);
        }
      } catch {
        if (!cancelled) setVehicle(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eid, tick]);

  return { vehicle, lastReading, loading };
}

// ----- Mutations ------------------------------------------------------------

/**
 * Create or update a vehicle, and RETURN the saved row.
 *
 * On the create path the patch carries no vehicle_id — the id does not exist
 * until the trigger mints it — so the caller cannot know what was created
 * unless the write hands it back. `.select('*').single()` is PostgREST's
 * returning clause; without it the "Add vehicle" flow would have to refetch the
 * whole fleet and guess which row is new.
 */
export async function saveVehicle(
  id: string | null,
  patch: VehicleWritePatch,
): Promise<VehicleRow> {
  const { data, error } = id
    ? await supabase.from('vehicles').update(patch).eq('vehicle_id', id).select('*').single()
    : await supabase.from('vehicles').insert(patch).select('*').single();
  if (error) throw error;
  return data as VehicleRow;
}

// saveMachine() was here and is GONE, not deprecated. floor_machines revokes
// INSERT/UPDATE from `authenticated` as of this PR, so a direct table write
// from the browser fails on privilege — a dead writer left in place is just a
// trap for the next author (TCSD-0047). Use saveMachineSpec / moveMachine /
// setMachineStatus from ./use-asset-location instead; they write the movement
// event alongside the projection.

export async function saveAssetCost(id: string | null, patch: Record<string, unknown>): Promise<void> {
  const { error } = id
    ? await supabase.from('asset_costs').update(patch).eq('cost_id', id)
    : await supabase.from('asset_costs').insert(patch);
  if (error) throw error;
}

/** Clear the needs_review flag on a cost (review queue "resolve"). Never blocks. */
export async function clearCostReview(costId: string): Promise<void> {
  const { error } = await supabase
    .from('asset_costs')
    .update({ needs_review: false })
    .eq('cost_id', costId);
  if (error) throw error;
}

/**
 * Submit an odometer reading from the app (source='manual'). Only the reading
 * is written: the database copies it onto the vehicle's mileage and
 * last_mileage_* (trg_vehicle_mileage_follows_reading, item #1011). This used to
 * update the vehicle itself, which a driver may not do, so a driver's reading
 * left the vehicle stale with no error. Plausibility is a soft warning in the
 * UI — we record reality and never hard-block.
 */
export async function submitOdometer(
  vehicleId: string,
  odometer: number,
  eid: string | null,
): Promise<void> {
  await commitWrite({
    label: 'save the odometer reading',
    write: supabase
      .from('vehicle_mileage_readings')
      .insert({ vehicle_id: vehicleId, odometer, reported_by_eid: eid, source: 'manual' })
      .select('reading_id'),
    touches: ['vehicle_mileage_readings', 'vehicles'],
  });
}
